import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Mail } from "lucide-react";
import { FAQSchema } from './FAQSchema';

interface EnhancedFAQsProps {
  title?: string;
  faqs?: Array<{ question: string; answer: string; }>;
  showSchema?: boolean;
}

// Enhanced FAQs with conversational, AI-prompt style questions
const DEFAULT_ENHANCED_FAQS = [
  {
    question: "Are you a broker or agent representing PEO companies?",
    answer: "No, we're not a traditional broker. The PEO App is an AI-powered technology platform that uses patent-pending algorithms to match organizations with PEOs based on 100+ data points. Unlike brokers who may push certain providers for higher commissions, our AI provides unbiased matching focused solely on finding the best fit for your specific needs. We do not charge anything to our SMB customers, and will willingly disclose the estimated compensation amounts from all presented providers, ensuring neutral recommendations and transparency."
  },
  {
    question: "What's the best PEO for small organizations?",
    answer: "The best PEO depends on your specific needs, but top factors include ESAC accreditation, industry expertise, technology platform, and pricing transparency. Small organizations often benefit from PEOs that offer flexible packages, dedicated support, and competitive benefits rates. Use The PEO App to compare multiple providers and find the right match for your organization size and industry."
  },
  {
    question: "How much does a PEO typically cost per employee?",
    answer: "PEO costs typically range from $1,500 to $3,000 per employee annually, or 2-12% of gross payroll. Pricing varies based on organization size, industry risk, benefit selections, and service level. Smaller organizations often pay higher per-employee rates, while larger organizations get better pricing. The PEO App helps you compare actual quotes from multiple providers to find competitive rates."
  },
  {
    question: "What's the difference between a PEO and an ASO?",
    answer: "A PEO (Professional Employer Organization) becomes your co-employer and takes on full HR responsibilities and liability, while an ASO (Administrative Services Only) provider just handles administrative tasks without becoming a co-employer. PEOs offer better benefits access and compliance protection but require more control sharing. ASOs give you more control but less comprehensive service."
  },
  {
    question: "Should I use a PEO if I only have 10 employees?",
    answer: "Yes, small organizations with 10 employees often see the biggest benefits from PEOs. You'll gain access to enterprise-level benefits, reduce compliance risks, and often save money on workers' compensation and health insurance. The administrative burden relief alone can free up significant time for organizational growth. The PEO App specializes in finding PEOs that work well for small organizations."
  },
  {
    question: "How do I know if a PEO is legitimate and reliable?",
    answer: "Look for ESAC accreditation, which requires strict financial and operational standards. Check their financial stability, client retention rates, technology capabilities, and compliance track record. Avoid PEOs with poor reviews, lack of transparency, or pushy sales tactics. The PEO App only partners with vetted, reputable providers to ensure quality matches."
  },
  {
    question: "Can I switch PEOs if I'm not satisfied?",
    answer: "Yes, you can switch PEOs, though it requires careful planning and typically has a 30-90 day transition period. Consider timing around benefit renewals and payroll cycles. Most reputable PEOs will assist with transitions. The PEO App can help you find a new provider and manage the transition process to minimize disruption."
  },
  {
    question: "What happens to my employees' benefits if I leave a PEO?",
    answer: "When leaving a PEO, you'll need to establish new benefit plans since employees lose access to the PEO's group plans. This often triggers COBRA obligations for departing employees. Plan transitions carefully around open enrollment periods when possible. Many organizations work with benefits brokers or new PEOs to ensure continuous coverage."
  },
  {
    question: "Do PEOs work for companies in highly regulated industries?",
    answer: "Yes, many PEOs specialize in regulated industries like healthcare, financial services, construction, and manufacturing. They bring deep compliance expertise and industry-specific HR solutions. Look for PEOs with relevant certifications, industry experience, and compliance track records. The PEO App can match you with providers that have expertise in your specific industry."
  },
  {
    question: "How long does it take to implement a PEO partnership?",
    answer: "PEO implementation typically takes 30-60 days from contract signing to full go-live. This includes data migration, system setup, employee communications, and benefit enrollments. Complex organizations or those with unique needs may take longer. The PEO App helps streamline the selection process so you can focus on smooth implementation."
  },
  {
    question: "What should I ask during PEO sales presentations?",
    answer: "Ask about their client retention rate, implementation timeline, technology platform capabilities, compliance track record, and total cost breakdown. Request client references, understand their service model, and clarify what happens if you need to terminate. The PEO App provides guidance on key questions and helps you compare responses from multiple providers."
  }
];

export const EnhancedFAQs = ({ 
  title = "Frequently Asked Questions About PEOs", 
  faqs = DEFAULT_ENHANCED_FAQS,
  showSchema = true 
}: EnhancedFAQsProps) => {
  return (
    <div className="max-w-4xl mx-auto">
      {showSchema && <FAQSchema faqs={faqs} />}
      
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold mb-4">{title}</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Get expert answers to the most common questions about choosing and working with a PEO
        </p>
      </div>

      <Accordion type="single" collapsible className="w-full space-y-4">
        {faqs.map((faq, index) => (
          <AccordionItem 
            key={index} 
            value={`faq-${index}`}
            className="bg-background border rounded-lg px-6 py-2 shadow-sm hover:shadow-md transition-shadow"
          >
            <AccordionTrigger className="text-left hover:no-underline group">
              <span className="text-lg font-semibold group-hover:text-primary transition-colors">
                {faq.question}
              </span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed pt-2 pb-4">
              {faq.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="mt-12 text-center">
        <div className="bg-muted/50 rounded-lg p-6">
          <p className="text-sm text-muted-foreground mb-4">
            Still have questions? Our PEO experts are here to help.
          </p>
          <Button
            variant="outline"
            size="lg"
            asChild
            className="gap-2"
          >
            <a href="mailto:info@thepeoapp.com">
              <Mail className="h-4 w-4" />
              Contact us for personalized guidance
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
};